import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hexToPixel, getHexCorners, generateLevel1Hexes, hexKey, type HexCoord, type PixelCoord } from '@/utils/hexGrid';
import { initializePlots } from '@/utils/initializePlots';
import type { Plot } from '@/types';
import { useTerrainIcons } from '@/hooks/useTerrainIcons';
import { useAvatarSprites } from '@/hooks/useAvatarSprites';
import { useRoverSprites } from '@/hooks/useRoverSprites';
import { selectZoom, selectPan, setZoom, setPan, selectIsInteractionEnabled, selectShowLandingPad } from '@/store/uiSlice';
import { fitBoardZoom } from '@/utils/fitBoardZoom';
import './HexGrid.css';

interface HexGridProps {
  plots: Plot[];
  players?: Array<{ 
    playerId: string; 
    playerNumber: number; 
    color: string;
    position?: { q: number; r: number; s: number }; // Avatar position
    rovers?: number; // Total rovers
    deployedRovers?: Array<any>; // Deployed rovers
    _roverPosition?: { q: number; r: number; s: number }; // Rover trailing position (during walking)
    _isWalking?: boolean; // Whether this player is currently walking
  }>; // Player data with positions (for rendering avatars)
  // All players' color info for plot ownership borders (separate from rendered players)
  playerColors?: Record<string, string>; // playerId -> color name
  onHexClick?: (plot: Plot) => void;
  onHexDoubleClick?: (plot: Plot) => void; // NEW: Double-click callback for rover placement
  onHexHover?: (plot: Plot | null) => void;
  currentPlayerId?: string;
  levelId?: string;
  width?: number;
  height?: number;
  hideBaseIcon?: boolean; // Hide base icon (e.g., during start_game animation)
  highlightedPlotId?: string | null; // Currently highlighted plot (M.U.L.E. style)
  focusMode?: boolean; // Land claim: the board fills the screen
  fitCamera?: boolean; // Animate to a fitted zoom; false eases back to 1
  productionPills?: Record<string, { resource: string; count: number }>;
}

/**
 * Terrain colors for Level 1 (Lunar Outpost)
 * Based on LEVEL_DESIGN.md terrain types
 */
const TERRAIN_COLORS: Record<string, string> = {
  lunar: '#8B8B8B', // Mare (Plains) - Gray lunar surface
  highland: '#A0A0A0', // Highlands - Lighter gray, elevated
  crater: '#5A5A5A', // Craters - Darker gray, shadows/water pockets
  mountain: '#B0B0B0', // Mountains - Light gray, peaks
  regolith: '#6B6B6B', // Regolith Fields - Medium gray, fine dust
  base: '#C0C0C0', // Base - Light gray, starting outpost
  // Legacy support
  valley: '#6B6B6B', // Valley - Medium gray (maps to regolith)
};

/**
 * Define how many icons to show based on terrain type
 * This simulates terrain "density" or "richness"
 */
const TERRAIN_ICON_COUNTS: Record<string, number> = {
  lunar: 1, // Plains - sparse
  highland: 2, // Elevated areas - moderate features
  crater: 3, // Craters - can show multiple impact sites
  mountain: 2, // Peaks - moderate
  regolith: 2, // Dust fields - moderate coverage
  base: 1, // Always one base icon
  valley: 1, // Valleys - sparse
};

/**
 * Minimum icon counts per terrain (to allow variability)
 */
const TERRAIN_ICON_MIN_COUNTS: Record<string, number> = {
  lunar: 0,
  highland: 1,
  crater: 1,
  mountain: 1,
  regolith: 0,
  base: 1,
  valley: 0,
};

/**
 * Default icon sizes (in pixels at zoom 1.0) per terrain type
 * These keep the sprites readable while varying the density visually
 */
const TERRAIN_ICON_SIZES: Record<string, number> = {
  lunar: 18,
  highland: 20,
  crater: 22,
  mountain: 21,
  regolith: 19,
  base: 65,
  valley: 18,
};

/**
 * Seeded random number generator for consistent icon positions
 * Uses a better hash-based approach for more varied randomness
 */
class SeededRandom {
  private state: number;
  
  constructor(seed: number) {
    // Use a better hash function to initialize state
    this.state = this.hash(seed);
  }
  
  private hash(n: number): number {
    // xorshift hash for better distribution
    n = ((n >>> 16) ^ n) * 0x45d9f3b;
    n = ((n >>> 16) ^ n) * 0x45d9f3b;
    n = (n >>> 16) ^ n;
    return n >>> 0; // Convert to unsigned 32-bit
  }
  
  next(): number {
    // Use xorshift for better random distribution
    this.state ^= this.state << 13;
    this.state ^= this.state >>> 17;
    this.state ^= this.state << 5;
    const unsignedState = this.state >>> 0;
    return unsignedState / 0xffffffff;
  }
  
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }
}

function createSeededRandom(hex: HexCoord, offset = 0): SeededRandom {
  const s = -hex.q - hex.r;
  const seedBase = hex.q * 7919 + hex.r * 6547 + s * 5479 + offset * 104729;
  return new SeededRandom(Math.abs(Math.floor(seedBase)) || 1);
}

/**
 * Generate random icon positions within a hex tile
 * Returns consistent positions based on seed (hex coordinates)
 */
function generateIconPositions(
  hex: HexCoord,
  count: number,
  hexSize: number,
  iconSize: number
): Array<{ x: number; y: number; scale: number }> {
  const rng = createSeededRandom(hex, 3);

  const positions: Array<{ x: number; y: number; scale: number }> = [];

  // Keep icons comfortably inside the hex. hexSize is the radius.
  const margin = iconSize * 0.35;
  const safeRadius = Math.max(hexSize - margin, iconSize * 0.6);

  for (let i = 0; i < count; i++) {
    let x: number | null = null;
    let y: number | null = null;
    let attempts = 0;
    const maxAttempts = 60;
    const minDistance = Math.max(iconSize * 0.55, 6);
    const minScale = 0.7;
    const maxScale = 2.0; // Up to 100% larger
    const randomScale = minScale + Math.pow(rng.next(), 1.4) * (maxScale - minScale);

    while (attempts < maxAttempts) {
      const angle = rng.next() * Math.PI * 2;
      const distance = Math.sqrt(rng.next()) * safeRadius;
      const candidateX = Math.cos(angle) * distance;
      const candidateY = Math.sin(angle) * distance;

      const tooClose = positions.some(pos => {
        const dx = pos.x - candidateX;
        const dy = pos.y - candidateY;
        const existingRadius = (iconSize * pos.scale) / 2;
        const candidateRadius = (iconSize * randomScale) / 2;
        const requiredSpacing = Math.max(minDistance, (existingRadius + candidateRadius) * 0.6);
        return Math.sqrt(dx * dx + dy * dy) < requiredSpacing;
      });

      if (!tooClose) {
        x = candidateX;
        y = candidateY;
        break;
      }

      attempts++;
    }

    if (x === null || y === null) {
      // Fall back to a simple placement if we couldn't find a good spot
      x = 0;
      y = 0;
    }

    positions.push({ x, y, scale: randomScale });
  }

  return positions;
}

function getRandomIconCount(hex: HexCoord, terrain: string): number {
  const maxCount = TERRAIN_ICON_COUNTS[terrain] || 1;
  const minCount = TERRAIN_ICON_MIN_COUNTS[terrain] ?? Math.min(1, maxCount);

  if (maxCount <= minCount) {
    return maxCount;
  }

  const rng = createSeededRandom(hex, 7);
  const range = maxCount - minCount + 1;
  const randomValue = Math.min(rng.next(), 0.999999999);
  const index = Math.floor(randomValue * range);
  return minCount + index;
}

/**
 * Player color mapping from backend color names to hex codes
 * These match the player1-8bit.png through player6-8bit.png icon colors
 * Includes both standard color names and additional variants used by create_game.py
 */
const PLAYER_COLOR_MAP: Record<string, string> = {
  // Standard color names (used by create_debug_game.py)
  'red': '#FF6B6B',      // Player 1 - Red
  'green': '#51E980',    // Player 2 - Bright Green
  'blue': '#6B9EFF',     // Player 3 - Light Blue
  'yellow': '#FFD700',   // Player 4 - Yellow/Gold
  'orange': '#FFA500',   // Player 5 - Orange
  'purple': '#B794F4',   // Player 6 - Purple
  // Additional color names (used by create_game.py)
  'cyan': '#00CCFF',     // Cyan/Turquoise
  'magenta': '#FF00FF',  // Magenta/Pink
};

/**
 * Smooth position interpolation system for avatars and rovers
 * Tracks target positions and smoothly animates to them
 */
interface PositionState {
  current: PixelCoord; // Current rendered position
  target: PixelCoord;  // Target position
  velocity: { x: number; y: number }; // For physics-based movement (rover inertia)
  lastUpdate: number;  // Timestamp of last update
}

const playerPositions = new Map<string, PositionState>();

export default function HexGrid({
  plots,
  players = [],
  playerColors = {}, // Map of playerId -> color name for plot ownership borders
  onHexClick,
  onHexDoubleClick, // NEW: Double-click callback
  onHexHover,
  currentPlayerId,
  levelId = 'lunar',
  width: layoutWidth = 800,
  height: layoutHeight = 600,
  hideBaseIcon = false,
  highlightedPlotId = null,
  focusMode = false,
  fitCamera = false,
  productionPills,
}: HexGridProps) {
  const [width, setWidth] = useState(layoutWidth);
  const [height, setHeight] = useState(layoutHeight);
  // Debug: Log when highlightedPlotId changes
  useEffect(() => {
    if (highlightedPlotId) {
      console.log('🟦 HexGrid received highlightedPlotId:', highlightedPlotId);
    }
  }, [highlightedPlotId]);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef(1);
  const zoomFrameRef = useRef<number | null>(null);
  const wasFocusedRef = useRef(false);
  const lastZoomTargetRef = useRef<number | null>(null);
  const [hoveredHex, setHoveredHex] = useState<HexCoord | null>(null);
  const [mousePos, setMousePos] = useState<PixelCoord | null>(null);
  const [lastClickTime, setLastClickTime] = useState<number>(0); // Track last click time for double-click detection
  const [lastClickedHex, setLastClickedHex] = useState<HexCoord | null>(null); // Track last clicked hex
  const terrainIcons = useTerrainIcons(); // Load terrain icons with caching
  const { avatarSprites, loading: spritesLoading } = useAvatarSprites(); // Load avatar sprites
  const { roverSprites, loading: roverSpritesLoading } = useRoverSprites(); // Load rover sprites
  const [animationFrame, setAnimationFrame] = useState(0); // Current animation frame (0-3)
  const lastFrameTimeRef = useRef<number>(Date.now());
  const idleTimerRef = useRef<number>(0); // Timer for idle weight-shifting
  
  // Update animation frame based on movement state
  // Walking: cycle through frames 0-3 at 8 fps
  // Idle: show frame 0, occasionally shift to frame 1 (weight shift)
  useEffect(() => {
    const intervalId = setInterval(() => {
      setAnimationFrame((prev) => {
        // Check if any player is walking
        const anyPlayerWalking = players?.some(p => p._isWalking) || false;
        
        if (anyPlayerWalking) {
          // Walking animation: cycle through all 4 frames
          idleTimerRef.current = 0; // Reset idle timer
          return (prev + 1) % 4; // Cycle 0 -> 1 -> 2 -> 3 -> 0
        } else {
          // Idle animation: stay on frame 0, occasionally shift to frame 1
          idleTimerRef.current += 1;
          
          // Every ~3 seconds (24 ticks at 125ms), do a weight shift
          if (idleTimerRef.current % 24 === 0) {
            return 1; // Shift weight (frame 1)
          } else if (prev === 1) {
            return 0; // Return to standing (frame 0)
          }
          
          return 0; // Default idle pose
        }
      });
    }, 125); // 125ms per frame = 8 fps

    return () => clearInterval(intervalId);
  }, [players]);
  
  // Get zoom and pan state from Redux
  const dispatch = useDispatch();
  const zoom = useSelector(selectZoom);
  const pan = useSelector(selectPan);
  const isInteractionEnabled = useSelector(selectIsInteractionEnabled);
  const showLandingPad = useSelector(selectShowLandingPad);
  
  const panX = pan?.x || 0;
  const panY = pan?.y || 0;
  zoomRef.current = zoom;
  
  // Base center of canvas (for calculations) - must be defined before mapBounds
  const baseCenterX = width / 2;
  const baseCenterY = height / 2;
  
  /**
   * Get interpolated position for smooth animation
   * Uses linear interpolation (lerp) to smoothly transition between hex positions
   * Rovers use physics-based interpolation with inertia
   */
  const getInterpolatedPlayerPosition = useCallback((
    entityId: string,
    targetHex: HexCoord,
    isRover = false // Rovers get physics-based movement
  ): PixelCoord => {
    // Calculate base pixel position (at zoom 1.0, no pan)
    const basePixel = hexToPixel(targetHex, baseCenterX, baseCenterY);
    
    // Apply zoom and pan transformations (same as drawHex)
    const targetPixel: PixelCoord = {
      x: (basePixel.x - baseCenterX) * zoom + baseCenterX + panX,
      y: (basePixel.y - baseCenterY) * zoom + baseCenterY + panY,
    };
    const now = Date.now();
    
    let state = playerPositions.get(entityId);
    
    // Initialize if first time seeing this entity
    if (!state) {
      state = {
        current: { ...targetPixel },
        target: { ...targetPixel },
        velocity: { x: 0, y: 0 },
        lastUpdate: now
      };
      playerPositions.set(entityId, state);
      return targetPixel;
    }
    
    // Check if target has changed
    const targetChanged = Math.abs(state.target.x - targetPixel.x) > 1 || Math.abs(state.target.y - targetPixel.y) > 1;
    
    if (targetChanged) {
      // New target - update target position
      state.target = { ...targetPixel };
      state.lastUpdate = now;
    }
    
    // Calculate distance to target
    const dx = state.target.x - state.current.x;
    const dy = state.target.y - state.current.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // If very close to target, snap to it
    if (distance < 0.5) {
      state.current = { ...state.target };
      state.velocity = { x: 0, y: 0 };
      return state.current;
    }

    // A jump of more than about two hexes is a relocate (leaving the store),
    // not a step. Appear there with the mule instead of dragging a leash across the map.
    const hexDistance = distance / (zoom || 1);
    if (hexDistance > 180) {
      state.current = { ...state.target };
      state.velocity = { x: 0, y: 0 };
      return state.current;
    }
    
    if (isRover) {
      // Physics-based movement for rover (inertia + spring pull)
      // Rover lags behind and has momentum
      
      // If rover is far from target (avatar stopped and rover catching up), use stronger spring
      const isCatchingUp = distance > 40; // More than ~1 hex away = catching up mode
      const springStrength = isCatchingUp ? 0.12 : 0.06; // Stronger pull when catching up
      const damping = 0.88; // Higher damping = more inertia/sliding
      const maxSpeed = 3.0; // Cap speed so rover doesn't overshoot
      
      // Spring force pulls rover toward target
      const forceX = dx * springStrength;
      const forceY = dy * springStrength;
      
      // Apply force to velocity (acceleration)
      state.velocity.x += forceX;
      state.velocity.y += forceY;
      
      // Apply damping (friction/air resistance)
      state.velocity.x *= damping;
      state.velocity.y *= damping;
      
      // Cap velocity to prevent wild movements
      const speed = Math.sqrt(state.velocity.x ** 2 + state.velocity.y ** 2);
      if (speed > maxSpeed) {
        state.velocity.x = (state.velocity.x / speed) * maxSpeed;
        state.velocity.y = (state.velocity.y / speed) * maxSpeed;
      }
      
      // Update position based on velocity
      state.current.x += state.velocity.x;
      state.current.y += state.velocity.y;
    } else {
      // Simple lerp for avatar (smooth but direct)
      const lerpSpeed = 0.08;
      state.current.x += dx * lerpSpeed;
      state.current.y += dy * lerpSpeed;
    }
    
    return state.current;
  }, [baseCenterX, baseCenterY, panX, panY, zoom]);
  
  // Animation frame ref for smooth rendering
  const animationFrameRef = useRef<number | null>(null);
  
  // Continuous render loop for smooth animation
  useEffect(() => {
    const animate = () => {
      // Trigger re-render if any player is moving
      let needsUpdate = false;
      playerPositions.forEach((state) => {
        const dx = state.target.x - state.current.x;
        const dy = state.target.y - state.current.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance > 0.5) {
          needsUpdate = true;
        }
      });
      
      if (needsUpdate) {
        // Force canvas re-render by updating a dummy state
        setMousePos(prev => prev ? { ...prev } : null);
      }
      
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    
    animationFrameRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<PixelCoord | null>(null);
  const [dragStartPan, setDragStartPan] = useState<PixelCoord>({ x: 0, y: 0 });
  
  // Preload terrain icon images for Canvas drawing
  const [terrainImages, setTerrainImages] = useState<Record<string, HTMLImageElement>>({});
  const [productionImages, setProductionImages] = useState<Record<string, HTMLImageElement>>({});
  
  useEffect(() => {
    const loadImages = async () => {
      const images: Record<string, HTMLImageElement> = {};
      
      for (const [terrain, dataUri] of Object.entries(terrainIcons)) {
        if (dataUri) {
          const img = new Image();
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = dataUri;
          });
          images[terrain] = img;
        }
      }
      
      setTerrainImages(images);
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`🎨 HexGrid - Preloaded ${Object.keys(images).length} terrain images for canvas rendering`);
      }
    };
    
    if (Object.keys(terrainIcons).length > 0) {
      loadImages();
    }
  }, [terrainIcons]);

  useEffect(() => {
    let cancelled = false;
    const sources: Record<string, string> = {
      energy: '/icons/energy-8bit.png',
      food: '/icons/food-8bit.png',
      mining: '/icons/minerals-8bit.png',
      water: '/icons/water-8bit.png',
      helium3: '/icons/helium3-8bit.png',
      hydrogen: '/icons/hydrogen-8bit.png',
      oxygen: '/icons/oxygen-8bit.png',
    };
    const images: Record<string, HTMLImageElement> = {};
    Promise.all(Object.entries(sources).map(([type, src]) => new Promise<void>((resolve) => {
      const img = new Image();
      img.onload = () => {
        images[type] = img;
        resolve();
      };
      img.onerror = () => resolve();
      img.src = src;
    }))).then(() => {
      if (!cancelled) setProductionImages(images);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Generate hex coordinates for Level 1
  const hexCoords = generateLevel1Hexes();
  
  // Calculate actual map bounds (min/max pixel positions of all hexes)
  // Note: baseCenterX and baseCenterY are used here, so they must be defined above
  const mapBounds = useMemo(() => {
    if (hexCoords.length === 0) {
      return { minX: 0, maxX: 0, minY: 0, maxY: 0, width: 0, height: 0 };
    }
    
    // Use local variables to avoid closure issues
    const centerX = width / 2;
    const centerY = height / 2;
    
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    
    // Calculate bounds of all hexes at base zoom (1.0)
    hexCoords.forEach(hex => {
      const pixel = hexToPixel(hex, centerX, centerY);
      const hexSize = 40; // Base hex size
      const corners = getHexCorners(pixel, hexSize);
      
      corners.forEach(corner => {
        minX = Math.min(minX, corner.x);
        maxX = Math.max(maxX, corner.x);
        minY = Math.min(minY, corner.y);
        maxY = Math.max(maxY, corner.y);
      });
    });
    
    return {
      minX,
      maxX,
      minY,
      maxY,
      width: maxX - minX,
      height: maxY - minY,
    };
  }, [hexCoords, width, height]);

  const animateZoom = useCallback((target: number) => {
    if (zoomFrameRef.current !== null) cancelAnimationFrame(zoomFrameRef.current);
    const from = zoomRef.current;
    const start = performance.now();
    const duration = 700;
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const next = from + (target - from) * eased;
      zoomRef.current = next;
      dispatch(setZoom(next));
      if (t < 1) zoomFrameRef.current = requestAnimationFrame(step);
      else zoomFrameRef.current = null;
    };
    zoomFrameRef.current = requestAnimationFrame(step);
  }, [dispatch]);

  useEffect(() => {
    return () => {
      if (zoomFrameRef.current !== null) cancelAnimationFrame(zoomFrameRef.current);
    };
  }, []);

  useEffect(() => {
    if (!focusMode) {
      setWidth(layoutWidth);
      setHeight(layoutHeight);
      if (wasFocusedRef.current) {
        wasFocusedRef.current = false;
        lastZoomTargetRef.current = null;
        dispatch(setPan({ x: 0, y: 0 }));
        animateZoom(1);
      }
      return;
    }
    wasFocusedRef.current = true;
    if (!fitCamera) {
      lastZoomTargetRef.current = 1;
      dispatch(setPan({ x: 0, y: 0 }));
      animateZoom(1);
      return;
    }
    const el = containerRef.current;
    if (!el || mapBounds.width <= 0 || mapBounds.height <= 0) return;
    const apply = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width < 2 || rect.height < 2) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const nextWidth = Math.max(1, Math.round(rect.width * dpr));
      const nextHeight = Math.max(1, Math.round(rect.height * dpr));
      setWidth((current) => (Math.abs(current - nextWidth) < 2 ? current : nextWidth));
      setHeight((current) => (Math.abs(current - nextHeight) < 2 ? current : nextHeight));
      const target = fitBoardZoom(nextWidth, nextHeight, mapBounds.width, mapBounds.height);
      if (lastZoomTargetRef.current !== null && Math.abs(lastZoomTargetRef.current - target) < 0.02) return;
      lastZoomTargetRef.current = target;
      animateZoom(target);
    };
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => observer.disconnect();
  }, [focusMode, fitCamera, layoutWidth, layoutHeight, mapBounds.width, mapBounds.height, animateZoom, dispatch]);
  
  // Memoize displayPlots to prevent regeneration on every render
  // Only recalculate when plots or levelId changes
  const displayPlots: Plot[] = useMemo(() => {
    if (plots.length > 0) {
      return plots;
    }
    return initializePlots(levelId);
  }, [plots, levelId]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      const terrains = new Set(displayPlots.map(plot => plot.terrain));
      // console.log('🧭 HexGrid terrain types:', Array.from(terrains)); // Silenced
    }
  }, [displayPlots]);
  
  // Memoize plot hex map to prevent regeneration
  const plotHexMap = useMemo(() => {
    const map = new Map<string, HexCoord>();
    displayPlots.forEach((plot, index) => {
      // If plot has hex coordinates from server, use those
      if (plot.hexCoords) {
        map.set(plot.plotId, { q: plot.hexCoords.q, r: plot.hexCoords.r });
      } else if (index < hexCoords.length) {
        // Fallback to positional mapping (legacy)
        map.set(plot.plotId, hexCoords[index]);
      }
    });
    return map;
  }, [displayPlots, hexCoords]);

  /**
   * Get plot for a hex coordinate
   */
  const getPlotForHex = useCallback((hex: HexCoord): Plot | null => {
    for (const [plotId, plotHex] of plotHexMap.entries()) {
      if (hexKey(plotHex) === hexKey(hex)) {
        return displayPlots.find(p => p.plotId === plotId) || null;
      }
    }
    return null;
    }, [displayPlots, plotHexMap, zoom, panX, panY]);

  /**
   * Find hex at pixel position (accounting for zoom and pan)
   */
  const findHexAtPixel = useCallback((pixel: PixelCoord): HexCoord | null => {
    // Simple approach: find the closest hex center and verify it's within range
    // This is more reliable than point-in-polygon for small performance cost
    
    let closestHex: HexCoord | null = null;
    let closestDistance = Infinity;
    
    // The effective "click radius" should be slightly larger than the hex size
    // to account for gaps between hexes and make clicking easier
    const clickRadius = 40 * zoom * 1.1; // 10% larger than visual hex size
    
    // Check each hex by transforming its position the same way we draw it
    for (const hex of hexCoords) {
      // Get base hex position
      const baseHexPixel = hexToPixel(hex, baseCenterX, baseCenterY);
      
      // Apply the same transformation used when drawing
      const transformedHexPixel: PixelCoord = {
        x: (baseHexPixel.x - baseCenterX) * zoom + baseCenterX + panX,
        y: (baseHexPixel.y - baseCenterY) * zoom + baseCenterY + panY,
      };
      
      // Calculate distance from mouse to hex center
      const dx = pixel.x - transformedHexPixel.x;
      const dy = pixel.y - transformedHexPixel.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      // Track closest hex within click radius
      if (distance < clickRadius && distance < closestDistance) {
        closestDistance = distance;
        closestHex = hex;
      }
    }
    
    if (closestHex) {
      // console.log('✅ Found hex:', closestHex, 'Distance:', closestDistance.toFixed(1), 'Threshold:', clickRadius.toFixed(1));
    } else {
      // console.log('❌ No hex found within range');
    }
    
    return closestHex;
  }, [hexCoords, baseCenterX, baseCenterY, panX, panY, zoom]);

  /**
   * Draw a single hex (accounting for zoom and pan)
   */
  const drawHex = useCallback((
    ctx: CanvasRenderingContext2D,
    hex: HexCoord,
    plot: Plot | null,
    isHovered: boolean
  ) => {
    // Calculate base pixel position (at zoom 1.0, no pan)
    const basePixel = hexToPixel(hex, baseCenterX, baseCenterY);
    
    // Apply zoom and pan transformations
    const pixel: PixelCoord = {
      x: (basePixel.x - baseCenterX) * zoom + baseCenterX + panX,
      y: (basePixel.y - baseCenterY) * zoom + baseCenterY + panY,
    };
    
    // Adjust hex size for zoom (base size is 40, from hexGrid utils)
    const hexSize = 40 * zoom;
    const corners = getHexCorners(pixel, hexSize);

    // Determine fill color
    let fillColor = '#4A4A4A'; // Default unclaimed
    let strokeColor = '#666666';
    let strokeWidth = 1;

    if (plot) {
      // Use terrain color as base
      fillColor = TERRAIN_COLORS[plot.terrain] || '#8B8B8B';
      
      // Get owner ID from either ownerId or claimedBy structure
      const ownerId = plot.ownerId || plot.claimedBy?.playerId;
      
      // A plot somebody already owns keeps its owner border. The ring only visits open land.
      const isHighlighted = highlightedPlotId === plot.plotId && !ownerId;
      if (isHighlighted) {
        strokeColor = '#00FFFF';
        strokeWidth = 4;
      }

      // If owned, show player color border. The highlight is drawn again on top.
      if (ownerId) {
        // First try playerColors map (includes ALL players, not just rendered ones)
        let playerColorName = playerColors[ownerId];
        
        // Fallback: try to find in rendered players array
        if (!playerColorName) {
          const ownerPlayer = players.find(p => p.playerId === ownerId);
          playerColorName = ownerPlayer?.color;
        }
        
        const playerColorHex = playerColorName ? PLAYER_COLOR_MAP[playerColorName] : null;
        
        if (playerColorHex) {
          strokeColor = playerColorHex;
          strokeWidth = 5; // Thicker for better visibility
        } else {
          // Fallback if color not found - use a default based on whether it's current player
          strokeColor = ownerId === currentPlayerId ? '#FFD700' : '#FF6B6B';
          strokeWidth = 5;
        }
      }
      if (isHighlighted) {
        strokeColor = '#00FFFF';
        strokeWidth = 4;
      }
    }

    // Hover effect disabled - no hover highlighting during land claim phase
    if (false && isHovered && highlightedPlotId !== plot?.plotId) {
      fillColor = adjustColorBrightness(fillColor, 0.2);
      strokeColor = '#FFFFFF';
      strokeWidth = 2;
    }

    // Draw hex fill
    ctx.beginPath();
    ctx.moveTo(corners[0].x, corners[0].y);
    for (let i = 1; i < corners.length; i++) {
      ctx.lineTo(corners[i].x, corners[i].y);
    }
    ctx.closePath();
    ctx.fillStyle = fillColor;
    ctx.fill();

    // Draw border (if claimed or highlighted)
    // Use inner stroke to prevent overlap with adjacent tiles
    if (strokeWidth > 2) {
      // Calculate inset corners (8% smaller to create gap)
      const insetPercent = 0.92;
      const insetCorners = corners.map(corner => ({
        x: pixel.x + (corner.x - pixel.x) * insetPercent,
        y: pixel.y + (corner.y - pixel.y) * insetPercent,
      }));
      
      ctx.beginPath();
      ctx.moveTo(insetCorners[0].x, insetCorners[0].y);
      for (let i = 1; i < insetCorners.length; i++) {
        ctx.lineTo(insetCorners[i].x, insetCorners[i].y);
      }
      ctx.closePath();
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = strokeWidth * zoom;
      ctx.stroke();
    } else {
      // Standard stroke for non-claimed tiles
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = strokeWidth * zoom;
      ctx.stroke();
    }

    // Draw terrain icons (or text fallback if icon not loaded)
    if (plot) {
      // Skip base icon if hideBaseIcon is true (e.g., during start_game animation)
      const isBase = plot.terrain === 'base';
      if (isBase && hideBaseIcon) {
        // Show landing pad instead of base icon during animation
        if (showLandingPad) {
          const landingPadImage = terrainImages['landingPad'];
          if (landingPadImage) {
            const iconSize = 27.5 * zoom; // 50% of base icon size (55 * 0.5)
            const iconOffsetY = -iconSize / 2 - 3;
            ctx.drawImage(landingPadImage, pixel.x - iconSize / 2, pixel.y + iconOffsetY, iconSize, iconSize);
          }
        }
        return; // Don't draw base icon
      }
      
      // Try to draw preloaded icon image
      const iconImage = terrainImages[plot.terrain];
      
      if (iconImage) {
        const logicalBaseSize = TERRAIN_ICON_SIZES[plot.terrain] || 18;
        if (isBase) {
          const iconSize = logicalBaseSize * zoom;
          const iconOffsetY = -iconSize / 2 - 3;
          ctx.drawImage(iconImage, pixel.x - iconSize / 2, pixel.y + iconOffsetY, iconSize, iconSize);
        } else {
          // Use server-provided icon placements if available, otherwise generate client-side
          let positions: Array<{ x: number; y: number; scale: number }>;
          
          if (plot.iconPlacements && plot.iconPlacements.length > 0) {
            // Use server-provided positions (for multiplayer consistency)
            positions = plot.iconPlacements;
            // Note: Debug logging removed to reduce console noise
          } else {
            // Fallback to client-side generation (legacy/testing)
            const iconCount = getRandomIconCount(hex, plot.terrain);

            if (iconCount > 0) {
              positions = generateIconPositions(hex, iconCount, hexSize, logicalBaseSize);
              // Note: Debug logging removed to reduce console noise
            } else {
              positions = [];
            }
          }

          if (positions.length > 0) {
            const iconSize = logicalBaseSize * zoom;

            positions.forEach(pos => {
              const scaledSize = iconSize * pos.scale;
              const iconX = pixel.x + pos.x * zoom - scaledSize / 2;
              const iconY = pixel.y + pos.y * zoom - scaledSize / 2;
              ctx.drawImage(iconImage, iconX, iconY, scaledSize, scaledSize);
            });
          }
        }
      } else {
        // Fallback to text if icon not loaded yet
        const terrainAbbrMap: Record<string, string> = {
          lunar: 'MA',
          highland: 'HI',
          crater: 'CR',
          mountain: 'MO',
          regolith: 'RE',
          base: 'BA',
          valley: 'VA',
        };
        const terrainAbbr = terrainAbbrMap[plot.terrain] || plot.terrain.substring(0, 2).toUpperCase();
        
        // Determine text color based on background brightness
        const hex = fillColor.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        const textColor = brightness > 180 ? '#333333' : '#FFFFFF';
        
        ctx.fillStyle = textColor;
        ctx.font = '10px monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(terrainAbbr, pixel.x, pixel.y - 15);
      }
      
      // Show owner indicator (only if no deployed rover)
      const hasDeployedRover = plot.rover && plot.rover.type;
      if (plot.ownerId && !hasDeployedRover) {
        ctx.fillStyle = '#FFD700';
        ctx.fillText('●', pixel.x, pixel.y + 10);
      }
      
      // The hex border is the owner's color. The icon is what this plot produces.
      if (hasDeployedRover) {
        const roverType = plot.rover!.type;
        const icon = productionImages[roverType];
        const iconSize = 26 * zoom;
        if (icon) {
          ctx.drawImage(icon, pixel.x - iconSize / 2, pixel.y - iconSize / 2, iconSize, iconSize);
        }
      }
    }

    const pillKey = plot?.plotId || (plot?.id != null ? String(plot.id) : '');
    const pill = pillKey && productionPills ? productionPills[pillKey] : undefined;
    if (pill && pill.count > 0) {
      const pillColor: Record<string, string> = {
        energy: '#FFD700',
        food: '#3DDC67',
        mining: '#C4A574',
        minerals: '#C4A574',
        water: '#4DA6FF',
        helium3: '#C084FC',
        oxygen: '#7FDBFF',
        hydrogen: '#E8E8E8',
      };
      const count = Math.max(1, Math.min(4, pill.count));
      const pillW = 12 * zoom;
      const pillH = 7 * zoom;
      const gap = 3 * zoom;
      const total = count * pillW + (count - 1) * gap;
      let pillX = pixel.x - total / 2;
      const pillY = pixel.y - 22 * zoom;
      const color = pillColor[pill.resource] || '#FFFFFF';
      for (let i = 0; i < count; i += 1) {
        const radius = pillH / 2;
        ctx.beginPath();
        ctx.moveTo(pillX + radius, pillY);
        ctx.arcTo(pillX + pillW, pillY, pillX + pillW, pillY + pillH, radius);
        ctx.arcTo(pillX + pillW, pillY + pillH, pillX, pillY + pillH, radius);
        ctx.arcTo(pillX, pillY + pillH, pillX, pillY, radius);
        ctx.arcTo(pillX, pillY, pillX + pillW, pillY, radius);
        ctx.closePath();
        ctx.fillStyle = color;
        ctx.fill();
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.45)';
        ctx.lineWidth = Math.max(1, zoom);
        ctx.stroke();
        pillX += pillW + gap;
      }
    }
  }, [baseCenterX, baseCenterY, zoom, panX, panY, currentPlayerId, plots, terrainImages, productionImages, hideBaseIcon, showLandingPad, productionPills]);

  /**
   * Adjust color brightness
   */
  const adjustColorBrightness = (color: string, factor: number): string => {
    // Simple brightness adjustment
    const num = parseInt(color.replace('#', ''), 16);
    const r = Math.min(255, Math.max(0, ((num >> 16) & 0xFF) + (factor * 255)));
    const g = Math.min(255, Math.max(0, ((num >> 8) & 0xFF) + (factor * 255)));
    const b = Math.min(255, Math.max(0, (num & 0xFF) + (factor * 255)));
    return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
  };

  // Render on changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Draw background
    ctx.fillStyle = '#1A1A1A';
    ctx.fillRect(0, 0, width, height);

    // Draw each hex
    hexCoords.forEach(hex => {
      const plot = getPlotForHex(hex);
      const isHovered = hoveredHex && hexKey(hoveredHex) === hexKey(hex);
      drawHex(ctx, hex, plot, isHovered || false);
    });

    // Draw avatars and rovers with smooth animation
    if (players && players.length > 0) {
      players.forEach((player, idx) => {
        if (player.position) {
          const { q, r, s } = player.position;
          
          // Get interpolated pixel position (smooth animation)
          const pixel = getInterpolatedPlayerPosition(player.playerId, { q, r, s });
          
          // Draw avatar sprite (if loaded) or fallback to circle
          const sprite = avatarSprites[player.playerNumber]?.[animationFrame];
          if (sprite && !spritesLoading) {
            // Draw sprite centered on pixel position
            const spriteSize = 32 * zoom; // 32px sprite scaled by zoom
            ctx.drawImage(
              sprite,
              pixel.x - spriteSize / 2,
              pixel.y - spriteSize / 2,
              spriteSize,
              spriteSize
            );
          } else {
            // Fallback to circle (if sprites not loaded yet)
            ctx.fillStyle = player.color;
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.arc(pixel.x, pixel.y, 20 * zoom, 0, Math.PI * 2);
            ctx.fill();
            ctx.stroke();
            
            // Draw player number
            ctx.fillStyle = '#FFFFFF';
            ctx.font = `bold ${Math.round(16 * zoom)}px Arial`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(`P${player.playerNumber}`, pixel.x, pixel.y);
          }
          
          // Draw rover if player has available rovers
          const availableRovers = (player.rovers || 0) - (player.deployedRovers?.length || 0);
          if (availableRovers > 0) {
            // Rover position: get from _roverPosition if available, otherwise calculate
            let roverPos;
            if (player._roverPosition) {
              roverPos = player._roverPosition;
            } else {
              // Fallback: one hex to the right
              roverPos = { q: q + 1, r: r, s: s - 1 };
            }
            
            const roverPixel = getInterpolatedPlayerPosition(player.playerId + '-rover', roverPos, true); // true = physics-based rover movement
            
            // Draw rover sprite (if loaded) or fallback to square
            const roverSprite = roverSprites[animationFrame];
            if (roverSprite && !roverSpritesLoading) {
              // Draw sprite centered on pixel position
              const spriteSize = 32 * zoom; // 32px sprite scaled by zoom
              ctx.drawImage(
                roverSprite,
                roverPixel.x - spriteSize / 2,
                roverPixel.y - spriteSize / 2,
                spriteSize,
                spriteSize
              );
            } else {
              // Fallback to square (if sprites not loaded yet)
              ctx.fillStyle = player.color;
              ctx.strokeStyle = '#FFFF00';
              ctx.lineWidth = 2;
              ctx.fillRect(roverPixel.x - 15 * zoom, roverPixel.y - 15 * zoom, 30 * zoom, 30 * zoom);
              ctx.strokeRect(roverPixel.x - 15 * zoom, roverPixel.y - 15 * zoom, 30 * zoom, 30 * zoom);
              
              // Draw "R" for rover
              ctx.fillStyle = '#FFFFFF';
              ctx.font = `bold ${Math.round(14 * zoom)}px Arial`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText('R', roverPixel.x, roverPixel.y);
            }
            
            // Draw leash connecting avatar to rover
            ctx.strokeStyle = player.color;
            ctx.lineWidth = 2;
            ctx.setLineDash([5, 5]);
            ctx.beginPath();
            ctx.moveTo(pixel.x, pixel.y);
            ctx.lineTo(roverPixel.x, roverPixel.y);
            ctx.stroke();
            ctx.setLineDash([]);
          }
        }
      });
    }
  }, [width, height, hexCoords, getPlotForHex, hoveredHex, drawHex, displayPlots, zoom, panX, panY, players, baseCenterX, baseCenterY, avatarSprites, animationFrame, spritesLoading, roverSprites, roverSpritesLoading]);

  /**
   * Handle mouse move (with pan support)
   */
  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isInteractionEnabled) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    
    // Account for CSS scaling: canvas might be displayed at a different size
    // than its actual pixel dimensions (width/height attributes)
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const pixel: PixelCoord = {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };

    // Handle panning (drag when zoomed in and map extends beyond viewport)
    if (isDragging && dragStart) {
      const deltaX = pixel.x - dragStart.x;
      const deltaY = pixel.y - dragStart.y;
      
      // Calculate new pan position
      const newPanX = dragStartPan.x + deltaX;
      const newPanY = dragStartPan.y + deltaY;
      
      // Constrain pan to keep map edges visible
      // Calculate map bounds at current zoom
      const zoomedMapWidth = mapBounds.width * zoom;
      const zoomedMapHeight = mapBounds.height * zoom;
      
      // Calculate center offset (map center relative to viewport center)
      const mapCenterX = (mapBounds.minX + mapBounds.maxX) / 2;
      const mapCenterY = (mapBounds.minY + mapBounds.maxY) / 2;
      const centerOffsetX = mapCenterX - baseCenterX;
      const centerOffsetY = mapCenterY - baseCenterY;
      
      // Calculate max pan based on actual map bounds
      const maxPanX = Math.max(0, (zoomedMapWidth - width) / 2 + centerOffsetX * zoom);
      const maxPanY = Math.max(0, (zoomedMapHeight - height) / 2 + centerOffsetY * zoom);
      const minPanX = -maxPanX;
      const minPanY = -maxPanY;
      
      const constrainedPanX = Math.max(minPanX, Math.min(maxPanX, newPanX));
      const constrainedPanY = Math.max(minPanY, Math.min(maxPanY, newPanY));
      dispatch(setPan({ x: constrainedPanX, y: constrainedPanY }));
      
      return; // Don't process hex hover while dragging
    }

    // Check bounds
    if (pixel.x < 0 || pixel.x > width || pixel.y < 0 || pixel.y > height) {
      if (hoveredHex) {
        setHoveredHex(null);
        setMousePos(null);
        if (onHexHover) {
          onHexHover(null);
        }
      }
      return;
    }

    setMousePos(pixel);

    const hex = findHexAtPixel(pixel);
    
    if (hex) {
      // Only update if hex changed
      const currentHexKey = hexKey(hex);
      const previousHexKey = hoveredHex ? hexKey(hoveredHex) : null;
      
      if (currentHexKey !== previousHexKey) {
        setHoveredHex(hex);
        const plot = getPlotForHex(hex);
        if (onHexHover) {
          onHexHover(plot);
        }
      }
    } else {
      // Only clear if we had a hovered hex
      if (hoveredHex) {
        setHoveredHex(null);
        if (onHexHover) {
          onHexHover(null);
        }
      }
    }
  }, [findHexAtPixel, getPlotForHex, onHexHover, hoveredHex, width, height, isDragging, dragStart, dragStartPan, zoom, mapBounds, baseCenterX, baseCenterY, dispatch, isInteractionEnabled]);

  // Check if panning should be enabled (map extends beyond viewport at current zoom)
  // Must be defined before handleMouseDown which uses it
  const canPan = useMemo(() => {
    if (zoom <= 1.0) return false;
    
    // Calculate zoomed map dimensions
    const zoomedMapWidth = mapBounds.width * zoom;
    const zoomedMapHeight = mapBounds.height * zoom;
    
    // Pan is enabled if zoomed map is larger than viewport
    return zoomedMapWidth > width || zoomedMapHeight > height;
  }, [zoom, mapBounds.width, mapBounds.height, width, height]);

  /**
   * Handle mouse down (start pan)
   */
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isInteractionEnabled) return;
    // Only enable pan if map extends beyond viewport boundaries
    if (canPan) {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const rect = canvas.getBoundingClientRect();
      
      // Account for CSS scaling
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      
      const pixel: PixelCoord = {
        x: (e.clientX - rect.left) * scaleX,
        y: (e.clientY - rect.top) * scaleY,
      };
      
      setIsDragging(true);
      setDragStart(pixel);
      setDragStartPan({ x: panX, y: panY });
    }
  }, [canPan, panX, panY, isInteractionEnabled]);

  /**
   * Handle mouse up (end pan)
   */
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragStart(null);
  }, []);

  /**
   * Handle mouse leave
   */
  const handleMouseLeave = useCallback(() => {
    setHoveredHex(null);
    setMousePos(null);
    setIsDragging(false);
    setDragStart(null);
    if (onHexHover) {
      onHexHover(null);
    }
  }, [onHexHover]);

  /**
   * Handle click (only if not dragging)
   */
  const handleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (isDragging) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = rect.width > 0 ? canvas.width / rect.width : 1;
    const scaleY = rect.height > 0 ? canvas.height / rect.height : 1;
    const pixel: PixelCoord = {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };

    const hex = findHexAtPixel(pixel);
    console.log('🎯 Found hex:', hex);
    
    if (hex) {
      const plot = getPlotForHex(hex);
      if (!plot) return;

      const now = Date.now();
      const timeSinceLastClick = now - lastClickTime;
      const isSameHex = lastClickedHex &&
                        lastClickedHex.q === hex.q &&
                        lastClickedHex.r === hex.r &&
                        lastClickedHex.s === hex.s;

      if (timeSinceLastClick < 300 && isSameHex && onHexDoubleClick) {
        onHexDoubleClick(plot);
        setLastClickTime(0);
        setLastClickedHex(null);
      } else if (onHexClick) {
        onHexClick(plot);
        setLastClickTime(now);
        setLastClickedHex(hex);
      }
    }
  }, [findHexAtPixel, getPlotForHex, onHexClick, onHexDoubleClick, isDragging, lastClickTime, lastClickedHex]);

  /**
   * Handle zoom in
   */
  const handleZoomIn = useCallback(() => {
    if (!isInteractionEnabled) return;
    animateZoom(Math.min(3, zoomRef.current + 0.25));
  }, [animateZoom, isInteractionEnabled]);

  const handleZoomOut = useCallback(() => {
    if (!isInteractionEnabled) return;
    const next = Math.max(0.5, zoomRef.current - 0.25);
    animateZoom(next);
    if (next <= 1) dispatch(setPan({ x: 0, y: 0 }));
  }, [animateZoom, dispatch, isInteractionEnabled]);

  return (
    <div className="hex-grid-container" ref={containerRef}>
      <div className="zoom-controls" hidden={focusMode}>
        <button
          className="zoom-button zoom-in"
          onClick={handleZoomIn}
          disabled={zoom >= 3.0 || !isInteractionEnabled}
          title="Zoom In"
          aria-label="Zoom In"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="2" fill="none"/>
            <line x1="10" y1="6" x2="10" y2="14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <line x1="6" y1="10" x2="14" y2="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
        <button
          className="zoom-button zoom-out"
          onClick={handleZoomOut}
          disabled={zoom <= 0.5 || !isInteractionEnabled}
          title="Zoom Out"
          aria-label="Zoom Out"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="2" fill="none"/>
            <line x1="6" y1="10" x2="14" y2="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      </div>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        onMouseMove={handleMouseMove}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        className={`hex-grid-canvas ${canPan ? 'can-pan' : ''} ${isDragging ? 'dragging' : ''}`}
        style={{ cursor: isDragging ? 'grabbing' : canPan ? 'grab' : 'default' }}
      />
    </div>
  );
}

